import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Sparkles, Lightbulb, Gem, Target, MessageCircle, Users2, Globe2, BarChart3 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const Section = ({number, kicker, title, subtitle, items, image, reverse=false, children}) => (
  <section className="py-20 md:py-28 bg-[#050505] border-b border-white/6">
    <div className="container-premium">
      <div className={`grid lg:grid-cols-2 gap-14 lg:gap-20 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <motion.div
          initial={{opacity:0,y:24}}
          whileInView={{opacity:1,y:0}}
          viewport={{once:true,margin:'-80px'}}
          transition={{duration:.65}}
        >
          <p className="section-kicker mb-4">{number} · {kicker}</p>
          <h2 className="gs-display text-4xl md:text-6xl mb-6">{title}</h2>
          {subtitle && <p className="text-white/55 text-lg leading-relaxed mb-8">{subtitle}</p>}
          {items && (
            <div className="space-y-3">
              {items.map(x=>(
                <div key={x} className="flex gap-3 items-start text-white/65">
                  <Check size={17} className="text-[#d7dbe0] mt-1 shrink-0"/>
                  <span>{x}</span>
                </div>
              ))}
            </div>
          )}
          {children}
        </motion.div>
        <motion.div
          initial={{opacity:0,scale:.98}}
          whileInView={{opacity:1,scale:1}}
          viewport={{once:true,margin:'-80px'}}
          transition={{duration:.8}}
          className="gs-image-frame"
        >
          <img src={image} alt="" className="gs-photo"/>
          <div className="gs-image-sheen"/>
        </motion.div>
      </div>
    </div>
  </section>
);

export default function CreativeArtPage(){
  return (
    <Layout>
      <section className="gs-page-hero min-h-[78vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/brand-assets/events.webp" alt="" className="w-full h-full object-cover opacity-24 gs-photo-monochrome"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#020202] via-[#020202]/90 to-[#020202]/45"/>
        </div>
        <div className="gs-grid-overlay"/>
        <div className="container-premium relative z-10 pt-32 pb-20">
          <div className="max-w-5xl">
            <p className="section-kicker mb-5">CREATIVE ART</p>
            <h1 className="gs-display text-5xl md:text-7xl xl:text-8xl leading-[.95] mb-8">
              CREAMOS MARCAS QUE<br/>
              <span className="metal-text">SE VEN, SE ESCUCHAN Y SE RECUERDAN.</span>
            </h1>
            <p className="text-white/62 text-lg md:text-xl max-w-3xl leading-relaxed">
              Estrategia · Contenido · Producción · Posicionamiento · Experiencias
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="grid lg:grid-cols-[.9fr_1.1fr] gap-16 items-center">
            <div>
              <p className="section-kicker mb-4">02 · QUIÉNES SOMOS</p>
              <h2 className="gs-display text-4xl md:text-6xl mb-7">CREATIVIDAD + ESTRATEGIA + <span className="metal-text">TECNOLOGÍA</span></h2>
              <p className="text-white/62 text-lg leading-relaxed mb-5">
                GS Agency es una agencia creativa y estratégica especializada en transformar ideas, talentos y negocios en marcas con identidad, presencia y autoridad.
              </p>
              <p className="text-white/48 leading-relaxed">
                Integramos estrategia, branding, contenido, producción, comunicación digital, entretenimiento, experiencias y tecnología para construir marcas que conectan con audiencias reales y generan resultados.
              </p>
            </div>
            <div className="gs-image-frame">
              <img src="/brand-assets/creative-about.webp" alt="GS Agency" className="gs-photo"/>
              <div className="gs-image-sheen"/>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#090909]">
        <div className="container-premium">
          <p className="section-kicker mb-4">03 · NUESTRA VISIÓN</p>
          <h2 className="gs-display text-4xl md:text-6xl max-w-4xl mb-8">
            INSPIRAR, CONECTAR Y <span className="metal-text">LIDERAR.</span>
          </h2>
          <p className="text-white/55 text-lg max-w-3xl leading-relaxed mb-14">
            Ser la agencia de referencia en el mercado hispano por nuestra capacidad de crear, posicionar y producir marcas que inspiran, conectan y lideran.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 editorial-frame overflow-hidden">
            {[
              [Sparkles,'PASIÓN','Amamos lo que hacemos y eso se refleja en cada proyecto.'],
              [Lightbulb,'CREATIVIDAD','Ideas originales que transforman conceptos en experiencias únicas.'],
              [Target,'ESTRATEGIA','Planificación inteligente para posicionar marcas con propósito.'],
              [Gem,'EXCELENCIA','Alta calidad en cada detalle para superar expectativas siempre.'],
              [BarChart3,'IMPACTO','Generamos resultados reales que dejan huella.']
            ].map(([Icon,t,d])=>(
              <div className="gs-panel p-6 md:p-8 text-center" key={t}>
                <Icon size={22} className="text-[#d7dbe0] mx-auto mb-5"/>
                <h3 className="font-accent text-sm tracking-[.12em] text-white mb-3">{t}</h3>
                <p className="text-white/38 text-xs leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section
        number="04" kicker="BRAND & IDENTITY"
        title={<>CREAMOS TU MARCA <span className="metal-text">DESDE CERO</span></>}
        subtitle="De una idea a una marca lista para competir."
        image="/brand-assets/creative-brand.webp"
        items={['Análisis y propósito','Naming y storytelling','Identidad visual','Arquitectura de marca','Estrategia de posicionamiento','Ecosistema digital','Plan de comunicación']}
      />

      <Section
        number="05" kicker="MARCA PERSONAL"
        title={<>ESTRATEGIA DE <span className="metal-text">MARCA PERSONAL</span></>}
        subtitle="Convertimos tu experiencia en autoridad y tu historia en influencia."
        image="/brand-assets/personal-brand.webp"
        reverse
        items={['Definición de propuesta de valor','Posicionamiento y nicho','Imagen y presencia digital','Narrativa y comunicación','Estrategia de contenido','Construcción de autoridad']}
      />

      <Section
        number="06" kicker="CONTENT & SOCIAL"
        title={<>CONTENT STRATEGY <span className="metal-text">& SOCIAL MEDIA</span></>}
        subtitle="Estrategia de contenido que conecta, posiciona y vende."
        image="/brand-assets/social-media.webp"
        items={['Estrategia editorial','Reels & videos','Calendario de contenido','Community management','Campañas y pauta','Análisis y optimización']}
      />

      <Section
        number="07" kicker="AUDIOVISUAL"
        title={<>PODCAST & PRODUCCIÓN <span className="metal-text">AUDIOVISUAL</span></>}
        subtitle="Contenido de valor que educa, conecta y posiciona."
        image="/brand-assets/podcast.webp"
        reverse
        items={['Concepto y formato','Producción profesional','Grabación de video y audio','Edición y postproducción','Clips para redes sociales','Distribución multiplataforma']}
      />

      <Section
        number="08" kicker="ECOSISTEMA DIGITAL"
        title={<>WEB & ECOSISTEMA <span className="metal-text">DIGITAL</span></>}
        subtitle="Diseñamos experiencias digitales que reflejan tu marca y generan credibilidad."
        image="/brand-assets/digital-ecosystem.webp"
        items={['Sitios web a la medida','Landing pages','Integración de redes','SEO básico','Mantenimiento y soporte']}
      >
        <Link to="/technology" className="link-metal mt-8 inline-flex">Ver la división Technology <ArrowRight size={16}/></Link>
      </Section>

      <Section
        number="09" kicker="POSICIONAMIENTO"
        title={<>POSICIONAMIENTO Y <span className="metal-text">AUTORIDAD</span></>}
        subtitle="Te posicionamos como la opción obvia en tu mercado."
        image="/brand-assets/authority.webp"
        reverse
        items={['Estrategia de posicionamiento','Reputación digital','Prensa y relaciones públicas','Alianzas estratégicas','Contenido de autoridad']}
      />

      <Section
        number="10" kicker="EXPERIENCIAS"
        title={<>ENTRETENIMIENTO, EVENTOS <span className="metal-text">& EXPERIENCIAS</span></>}
        subtitle="Producimos experiencias memorables que conectan marcas y audiencias."
        image="/brand-assets/events.webp"
        items={['Producción de eventos','Festivales y conciertos','Lanzamientos y activaciones','Experiencias de marca','Booking y talento','Producción artística integral']}
      >
        <div className="flex flex-wrap gap-4 mt-8">
          <Link to="/eventos" className="btn-metal-outline">Explorar eventos</Link>
          <Link to="/talent" className="btn-metal-outline">Explorar talento</Link>
        </div>
      </Section>

      <section className="py-24 md:py-32 bg-[#090909]">
        <div className="container-premium">
          <p className="section-kicker mb-4">11 · NUESTRO MODELO 360°</p>
          <div className="grid lg:grid-cols-[.8fr_1.2fr] gap-14 items-center">
            <div className="model-orbit mx-auto">
              <div className="model-orbit-inner"><span>GS</span></div>
            </div>
            <div className="border-t border-white/15">
              {[
                ['01','ESTRATEGIA','Definimos tu marca y objetivos.'],
                ['02','IDENTIDAD','Creamos una marca con propósito.'],
                ['03','TECNOLOGÍA','Construimos la infraestructura digital que la hace operar.'],
                ['04','CONTENIDO','Producimos contenido que conecta.'],
                ['05','DISTRIBUCIÓN','Llevamos tu mensaje al público correcto.'],
                ['06','CONVERSIÓN','Conectamos audiencia, leads, ventas y datos.'],
                ['07','EXPERIENCIA','Creamos momentos que se recuerdan.'],
                ['08','RESULTADOS','Medimos, analizamos y escalamos.']
              ].map(([n,t,d])=>(
                <div key={n} className="grid grid-cols-[50px_.65fr_1.35fr] gap-5 py-5 border-b border-white/10">
                  <span className="text-white/26 text-xs tracking-[.2em]">{n}</span>
                  <strong className="font-accent text-sm tracking-[.1em]">{t}</strong>
                  <span className="text-white/42 text-sm">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <p className="section-kicker mb-4">12 · ¿POR QUÉ GS AGENCY?</p>
          <h2 className="gs-display text-4xl md:text-6xl max-w-4xl mb-14">
            VISIÓN, ESTRATEGIA Y <span className="metal-text">EXCELENCIA.</span>
          </h2>
          <div className="grid md:grid-cols-4 gap-px bg-white/10 editorial-frame overflow-hidden">
            {[
              ['EXPERIENCIA','Comprobada en emprendimientos y marcas.'],
              ['EQUIPO EXPERTO','Creativos, estrategas y productores.'],
              ['SOLUCIONES 360°','Todo lo que tu marca necesita en un solo lugar.'],
              ['RESULTADOS','Marcas posicionadas, reconocidas e impactantes.']
            ].map(([t,d])=>(
              <div key={t} className="gs-panel p-8 min-h-[220px]">
                <h3 className="font-accent tracking-[.1em] text-white mb-5">{t}</h3>
                <p className="text-white/42 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#090909]">
        <div className="container-premium">
          <p className="section-kicker mb-4">13 · ALGUNOS RUBROS CON LOS QUE TRABAJAMOS</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
            {['ARTISTAS & ENTRETENIMIENTO','EMPRESAS & EMPRENDEDORES','MARCA PERSONAL & LÍDERES','EVENTOS & FESTIVALES','LIFESTYLE & F&B'].map(x=>(
              <div className="model-chip min-h-[120px] flex items-center justify-center text-center" key={x}>{x}</div>
            ))}
          </div>
          <p className="text-center gs-display text-2xl md:text-3xl mt-12 text-white/72">Cada marca es única. Cada estrategia, también.</p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <p className="section-kicker mb-4">DIRECCIÓN</p>
          <h2 className="gs-display text-4xl md:text-6xl mb-16">VISIÓN CREATIVA Y <span className="metal-text">EMPRESARIAL</span></h2>

          <div className="grid lg:grid-cols-2 gap-10">
            <article className="editorial-frame overflow-hidden bg-[#070707]">
              <img src="/brand-assets/flavia-gyehl.webp" alt="Flavia Gyehl" className="w-full h-[420px] object-cover object-center"/>
              <div className="p-8 md:p-10">
                <p className="section-kicker mb-3">CEO & FOUNDER</p>
                <h3 className="gs-display text-4xl mb-3">FLAVIA <span className="metal-text">GYEHL</span></h3>
                <p className="text-white/70 text-sm tracking-[.12em] uppercase mb-6">Productora · Empresaria · Creadora de experiencias</p>
                <p className="text-white/48 leading-relaxed mb-4">
                  Más de 20 años de trayectoria en la industria del entretenimiento, producción artística, festivales, eventos y desarrollo de marcas.
                </p>
                <p className="text-white/48 leading-relaxed">
                  Lidera GS Agency con una visión estratégica, creativa y humana, convirtiendo ideas en experiencias y marcas en legado.
                </p>
              </div>
            </article>

            <article className="editorial-frame overflow-hidden bg-[#070707]">
              <img src="/brand-assets/jorge-salcido.webp" alt="Jorge Esteban Salcido Toledo" className="w-full h-[420px] object-cover object-center"/>
              <div className="p-8 md:p-10">
                <p className="section-kicker mb-3">CEO EJECUTIVO</p>
                <h3 className="gs-display text-4xl mb-5">JORGE ESTEBAN <span className="metal-text">SALCIDO TOLEDO</span></h3>
                <div className="space-y-2 text-white/48 text-sm">
                  {[
                    'Comunicación Visual e Ingeniería Industrial.',
                    'Especialización en Producción Audiovisual.',
                    '7 años en producción cinematográfica.',
                    'Fundador y director de Ego Management.',
                    'Experiencia con marcas nacionales e internacionales.',
                    'Productor ejecutivo e inversionista en eventos masivos +5,000 asistentes.',
                    'Presidente en México de Miss Bikini of the World.',
                    'Expertise: entretenimiento · audiovisual · talento · branding.'
                  ].map(x=><div className="flex gap-2" key={x}><Check size={14} className="text-[#d7dbe0] mt-1 shrink-0"/><span>{x}</span></div>)}
                </div>
                <p className="text-white/72 mt-6">Estrategia, producción y visión empresarial aplicadas a experiencias de alto impacto.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#090909]">
        <div className="container-premium">
          <div className="editorial-frame p-8 md:p-14 grid lg:grid-cols-[1fr_auto] gap-10 items-end">
            <div>
              <p className="section-kicker mb-4">14 · TRABAJEMOS JUNTOS</p>
              <h2 className="gs-display text-4xl md:text-6xl mb-5">¿LISTO PARA LLEVAR TU MARCA AL <span className="metal-text">SIGUIENTE NIVEL?</span></h2>
              <p className="text-white/48">Hagamos que tu marca se vea, se escuche y se recuerde.</p>
            </div>
            <Link to="/contact" className="btn-metal inline-flex items-center gap-2">Hablemos <ArrowRight size={17}/></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
